// Light/dark theme helpers. The active theme lives as a `dark` class on
// <html> (Tailwind `darkMode: "class"`); the choice is remembered in
// localStorage, falling back to the OS preference on first visit. A tiny
// inline script in index.html applies the same decision before first paint so
// the app never flashes the wrong theme.

export type Theme = "light" | "dark";

const STORAGE_KEY = "blplus-theme";

export function readStoredTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    // Private mode / storage disabled: fall through to the OS preference.
    return null;
  }
}

export function systemTheme(): Theme {
  return typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

// What the provider starts with: the saved choice, else the OS preference.
export function getInitialTheme(): Theme {
  return readStoredTheme() ?? systemTheme();
}

export function storeTheme(theme: Theme) {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Non-fatal: the choice just won't persist across reloads.
  }
}

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.style.colorScheme = theme;
}
